import { Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from "@mui/material"
import { useFormContext } from "react-hook-form"
import { useCallback, useState } from "react"
import moment, { Moment } from "moment"
import { useQuery } from "react-query"
import { enqueueSnackbar } from "notistack"
import axiosClient from "../../../../../lib/axios"
import { PesquisarRetencoes } from "../../../../../types/api/contabilidade/pesquisar-retencoes"
import { formatarInputValorMonetário } from "../../../../../helpers/masks"
import LoadingApiQuery from "../../../../LoadingApiQuery"
import BasicDatePicker from "../../../../BasicDatePicker"

export default function DialogAdicionar({
    open,
    handleClose,
    spec_name
}: DialogAdicionarProps) {

    const { getValues, setValue } = useFormContext()
    const prevRetencoes = getValues(`${spec_name}.retencoes`) || []

    const [valores, setValores] = useState<ValoresRetencao[]>([])

    const { isLoading } = useQuery({
        queryKey: ["contabilidade-pesquisar-retencoes"],
        queryFn: () => axiosClient.get("/contabilidade/pesquisar-retencoes"),
        onError: (error) => {
            console.log(error)
            enqueueSnackbar("Erro ao buscar retenções", {
                variant: "error"
            })
        },
        onSuccess: (data) => {
            if (prevRetencoes.length > 0) {
                setValores(prevRetencoes)
                return
            }
            const listaDados: PesquisarRetencoes[] = data.data.data
            const dadosFormatados: ValoresRetencao[] = listaDados.map((retencao) => ({
                retencao: retencao,
                vencimento: null,
                valor: '',
                checked: false
            }))
            setValores(dadosFormatados)
        },
        cacheTime: 0
    });

    const handleChangeChecked = (index: number) => {
        setValores(prev => {
            const novoValores = [...prev]
            novoValores[index] = {
                ...novoValores[index],
                vencimento: moment(),
                valor: 'R$ 0,00',
                checked: !novoValores[index].checked
            }
            return novoValores
        })
    }

    const handleChangeVencimento = useCallback((index: number, data: Moment | null) => {
        setValores(prev => {
            const novoValores = [...prev]
            novoValores[index] = {
                ...novoValores[index],
                vencimento: data
            }
            return novoValores
        })
    }, [])

    const handleChangeValor = useCallback((index: number, valor: string) => {
        setValores(prev => {
            const novoValores = [...prev]
            novoValores[index] = {
                ...novoValores[index],
                valor: formatarInputValorMonetário(valor)
            }
            return novoValores
        })
    }, [])

    const handleSubmit = () => {
        setValue(`${spec_name}.retencoes`, valores)
        handleClose()
    }

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="md"
        >
            <DialogTitle >
                Adicionar retenção
            </DialogTitle>

            <DialogContent>
                {isLoading && <LoadingApiQuery />}

                {
                    !isLoading &&
                    <TableContainer>
                        <Table aria-label="simple table">
                            <TableHead>
                                <TableRow>
                                    <TableCell><b>Descrição</b></TableCell>
                                    <TableCell align="right"></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {valores.map((row, index) => (
                                    <TableRow
                                        key={row.retencao?.id}
                                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                    >
                                        <TableCell component="th" scope="row">
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                <Checkbox
                                                    checked={row.checked}
                                                    onChange={() => handleChangeChecked(index)}
                                                    slotProps={{
                                                        input: { 'aria-label': 'controlled' }
                                                    }}
                                                    sx={{ padding: 0 }}
                                                />
                                                <Typography variant="body2">
                                                    {`${row.retencao?.id} - ${row.retencao?.descricao}`}
                                                </Typography>
                                            </Stack>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                <Box width="50%">
                                                    <BasicDatePicker
                                                        label="Vencimento"
                                                        sizeInput="small"
                                                        blockPastDates
                                                        value={row.vencimento}
                                                        onChange={(data) => handleChangeVencimento(index, data)}
                                                        formatString="DD/MM/YYYY"
                                                    />
                                                </Box>

                                                <Box width="50%">
                                                    <TextField
                                                        size="small"
                                                        label="Valor"
                                                        value={row.valor}
                                                        onChange={(e) => handleChangeValor(index, e.target.value)}
                                                    />
                                                </Box>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                }
            </DialogContent>

            <DialogActions>
                <Button
                    onClick={handleClose}
                >
                    Cancelar
                </Button>

                <Button
                    variant="contained"
                    onClick={handleSubmit}
                >
                    Confirmar
                </Button>
            </DialogActions>
        </Dialog>
    )
}

interface DialogAdicionarProps {
    open: boolean
    handleClose: () => void
    spec_name: string
}

export type ValoresRetencao = {
    retencao: PesquisarRetencoes | null,
    vencimento: Moment | null,
    valor: string,
    checked: boolean
}
